import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Calendar, Camera, MessageText1, Clock, ImportCurve, Kanban, Star1, Edit2, TaskSquare } from "iconsax-react";
import {
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  ModalForm,
  PdfViewer,
  Prompt,
  RadioButton,
  RadioGroup,
  Tabs,
} from "@/shared/ui";
import type { TabItem } from "@/shared/ui";
import { extractErrorMessage, formatDateDDMMYYYY } from "@/shared/utils";
import { useLocale } from "@/shared/hooks";
import { useLazyGetFSMPdfQuery } from "@/features/self/pending";
import type { Task, TaskStatus } from "../../../types";
import { useGetTaskAuditQuery } from "../../../api";
import { PRIORITY_MAP, TASK_STATUS_MAP, TASK_STATUSES } from "../../../consts";
import { generateAuditPdf, formatAuditRowsForPdf } from "../../../utils";
import TaskComments from "./TaskComments";
import TaskHistory from "./TaskHistory";
import TaskDetailSkeleton from "./TaskDetailSkeleton";
import { MoreIcon } from "@/shared/assets/icons";

interface Props {
  task?: Task;
  taskId: number;
  projectId: number;
  isLoading: boolean;
  isTaskFetching: boolean;
  statusOverride?: TaskStatus;
  projectName: string;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canManage: boolean;
  canComment: boolean;
  canUpdateOwnStatus?: boolean;
  currentUserId?: number | null;
  onStatusChange?: (taskId: number, newStatus: TaskStatus) => void;
  statusLabelMap: Record<string, string>;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  onViewBoard?: () => void;
}

export default function TaskDetailModal({
  task,
  taskId,
  projectId,
  isLoading,
  isTaskFetching,
  statusOverride,
  projectName,
  onClose,
  onEdit,
  onDelete,
  canManage,
  canComment,
  canUpdateOwnStatus,
  currentUserId,
  onStatusChange,
  statusLabelMap,
  isFavorite,
  onToggleFavorite,
  onViewBoard,
}: Props) {
  const { t } = useTranslation("TasksPage");
  const locale = useLocale();
  const [menuOpen, setMenuOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"comments" | "history">("comments");
  const [isExportingAudit, setIsExportingAudit] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [localFavorite, setLocalFavorite] = useState(isFavorite ?? false);

  useEffect(() => {
    setLocalFavorite(isFavorite ?? false);
  }, [isFavorite]);

  function handleFavoriteClick() {
    setLocalFavorite((prev) => !prev);
    onToggleFavorite?.();
  }

  const isOwnTask = task?.assigned_to === currentUserId;
  const canChangeStatus = canManage || (canUpdateOwnStatus && isOwnTask);

  const { data: auditData } = useGetTaskAuditQuery(taskId);
  const [fetchFsmPdf, { isFetching: isFsmPdfLoading }] = useLazyGetFSMPdfQuery();
  const [fsmPdfUrl, setFsmPdfUrl] = useState<string | null>(null);

  async function handleOpenFsmReport() {
    if (!task?.fsm_submission_id || isFsmPdfLoading) return;
    try {
      const result = await fetchFsmPdf(task.fsm_submission_id).unwrap();
      setFsmPdfUrl(result.pdf_url);
    } catch {
      setExportError(t("fsm.downloadError"));
    }
  }

  async function handleExportTaskAudit() {
    if (isExportingAudit || !auditData?.results.length) return;
    setIsExportingAudit(true);
    try {
      const rows = formatAuditRowsForPdf(auditData.results, t, locale);
      await generateAuditPdf({
        title: `${taskId}`,
        subtitle: t("export.taskAuditTitle"),
        columns: [
          t("export.auditColumns.date"),
          t("export.auditColumns.user"),
          t("export.auditColumns.action"),
          t("export.auditColumns.target"),
          t("export.auditColumns.changes"),
        ],
        rows,
        filename: `${taskId} — ${t("export.taskAuditTitle")}.pdf`,
        labels: {
          generatedAt: t("export.auditColumns.date"),
        },
      });
    } catch (err: unknown) {
      setExportError(extractErrorMessage(err, "messages.exportError", t));
    } finally {
      setIsExportingAudit(false);
    }
  }

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        id: "comments",
        label: t("taskDetail.tabComments"),
        count: task?.comments.length ?? 0,
      },
      {
        id: "history",
        label: t("taskDetail.tabHistory"),
      },
    ],
    [t, task?.comments.length],
  );

  const DESC_MAX_LENGTH = 200;

  const effectiveStatus = task ? (statusOverride ?? task.status) : undefined;

  return (
    <>
    <ModalForm
      icon={TaskSquare}
      iconClassName="background-neutral-fill inner-glow radial-glow text-white"
      onClose={onClose}

      width="w-5xl"
      closeButtonLabel={t("close", { ns: "Common" })}
      headerExtra={
        onToggleFavorite ? (
          <>
            <Button
              className="w-10! p-0! aspect-square flex justify-center items-center cursor-pointer content-base-secondary"
              variant="secondary"
              onClick={handleFavoriteClick}>
              <Star1
                size={20}
                color="currentColor"
                variant={localFavorite ? "Bold" : "Linear"}
                className={localFavorite ? "text-yellow-500" : ""}
              />
            </Button>
            <div className="w-px h-6 bg-alpha-black-10" />
          </>
        ) : undefined
      }
      footer={
        onViewBoard ? (
          <Button variant="secondary" size="md" className="w-full" onClick={onViewBoard}>
            <Kanban size={16} color="currentColor" />
            {t("taskDetail.viewOnBoard")}
          </Button>
        ) : undefined
      }>
      {isLoading || !task || task.id !== taskId ? (
        <TaskDetailSkeleton isGoal={task?.task_type === "kpi"} />
      ) : (
        <div className="flex flex-col">
          <div className="flex items-start justify-between gap-5 pb-5">
            <h3 className="text-display-2xs content-base-primary flex-1">{task.title}</h3>
            <div className="flex items-center gap-1.5 shrink-0">
              {canManage && (
                <>
                  <Button
                    isIconButton
                    variant="tertiary"
                    onClick={onEdit}
                    className="w-8! h-8! p-0! flex items-center justify-center radius-xs content-base-secondary!">
                    <Edit2 size={16} color="currentColor" />
                  </Button>
                  <Dropdown open={menuOpen} onClose={() => setMenuOpen(false)} align="right">
                    <Button
                      isIconButton
                      variant="tertiary"
                      onClick={() => setMenuOpen((prev) => !prev)}
                      className="w-8! h-8! p-0! flex items-center justify-center radius-xs content-base-secondary!">
                      <MoreIcon />
                    </Button>
                    <DropdownItem
                      onClick={() => {
                        setMenuOpen(false);
                        onDelete();
                      }}>
                      {t("taskDetail.delete")}
                    </DropdownItem>
                  </Dropdown>
                </>
              )}
            </div>
          </div>

          <div className="border-t surface-base-stroke mb-5" />

          <div className="flex gap-5 flex-1">
            <div className="flex-1 flex flex-col">
              <div className="pb-5">
                <p className="text-label-sm content-base-primary pb-2">{t("taskDetail.description")}</p>
                <p
                  className={cn(
                    "text-body-regular-sm",
                    task.description ? "content-base-secondary" : "content-base-low italic",
                    task.description && task.description.length > DESC_MAX_LENGTH && descExpanded && "cursor-pointer",
                  )}
                  onClick={
                    descExpanded && task.description && task.description.length > DESC_MAX_LENGTH
                      ? () => setDescExpanded(false)
                      : undefined
                  }>
                  {!task.description ? (
                    t("taskDetail.noDescription")
                  ) : descExpanded || task.description.length <= DESC_MAX_LENGTH ? (
                    task.description
                  ) : (
                    <>
                      {task.description.slice(0, DESC_MAX_LENGTH)}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => setDescExpanded(true)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setDescExpanded(true);
                        }}
                        className="content-base-secondary cursor-pointer ml-0.5">
                        ...
                      </span>
                    </>
                  )}
                </p>
              </div>

              <Tabs
                color="black"
                items={tabItems}
                activeId={activeTab}
                onChange={(id) => setActiveTab(id as "comments" | "history")}
              />
              <div className="mt-3">
                {activeTab === "comments" ? (
                  <TaskComments
                    projectId={projectId}
                    taskId={task.id}
                    comments={task.comments}
                    canComment={canComment}
                    isTaskFetching={isTaskFetching}
                    currentUserId={currentUserId}
                  />
                ) : (
                  <div>
                    <TaskHistory taskId={task.id} />
                    <div className="surface-base-fill pt-3 p-1">
                      <Button
                        variant="secondary"
                        size="md"
                        className="w-full"
                        onClick={handleExportTaskAudit}
                        disabled={isExportingAudit || !auditData?.results?.length}>
                        <ImportCurve size={16} color="currentColor" />
                        {t("export.downloadAuditPdf")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="w-2xs shrink-0">
              <div className="p-5 radius-lg border surface-component-stroke flex flex-col gap-5">
                <SidebarField label={t("taskDetail.project")}>
                  <p className="text-body-bold-sm content-base-primary">{projectName}</p>
                </SidebarField>

                <SidebarField label={t("taskDetail.status")}>
                  {canChangeStatus && onStatusChange && effectiveStatus ? (
                    <Dropdown
                      open={statusDropdownOpen}
                      onClose={() => setStatusDropdownOpen(false)}
                      direction="bottom"
                      align="left"
                      width="w-max">
                      <button type="button" onClick={() => setStatusDropdownOpen((prev) => !prev)}>
                        <Badge
                          variant="soft"
                          color={TASK_STATUS_MAP[effectiveStatus].color}
                          clickable
                          text={statusLabelMap[effectiveStatus] ?? t(TASK_STATUS_MAP[effectiveStatus].label)}
                        />
                      </button>
                      <div className="p-2">
                        <RadioGroup
                          name="task-detail-status"
                          value={effectiveStatus}
                          onChange={(val) => {
                            onStatusChange(task.id, val as TaskStatus);
                            setStatusDropdownOpen(false);
                          }}
                          wrapperClassName="flex flex-col gap-2">
                          {TASK_STATUSES.map((status) => (
                            <RadioButton
                              key={status}
                              value={status}
                              label={statusLabelMap[status] ?? t(TASK_STATUS_MAP[status].label)}
                            />
                          ))}
                        </RadioGroup>
                      </div>
                    </Dropdown>
                  ) : effectiveStatus ? (
                    <Badge
                      variant="soft"
                      color={TASK_STATUS_MAP[effectiveStatus].color}
                      text={statusLabelMap[effectiveStatus] ?? t(TASK_STATUS_MAP[effectiveStatus].label)}
                    />
                  ) : null}
                </SidebarField>

                {task.task_type !== "kpi" && (
                  <SidebarField label={t("taskDetail.priority")}>
                    <Badge
                      variant="soft"
                      color={PRIORITY_MAP[task.priority].color}
                      text={t(PRIORITY_MAP[task.priority].label)}
                    />
                  </SidebarField>
                )}

                {task.task_type !== "kpi" && (
                  <SidebarField label={t("taskDetail.assignee")}>
                    <p
                      className={cn(
                        "text-body-bold-sm",
                        task.assigned_to_name ? "content-base-primary" : "content-base-low",
                      )}>
                      {task.assigned_to_name || t("taskDetail.noAssignee")}
                    </p>
                  </SidebarField>
                )}

                <SidebarField label={t("taskDetail.createdAt")}>
                  <div className="flex items-center gap-1.5">
                    <Calendar size={14} color="currentColor" className="content-base-secondary" />
                    <span className="text-body-bold-sm content-base-primary">
                      {formatDateDDMMYYYY(task.created_at)}
                    </span>
                  </div>
                </SidebarField>

                {task.task_type !== "kpi" && (
                  <SidebarField label={t("taskDetail.dueDate")}>
                    <div className="flex items-center gap-1.5">
                      <Calendar size={14} color="currentColor" className="content-base-secondary" />
                      <span
                        className={cn("text-body-bold-sm", task.due_date ? "content-base-primary" : "content-base-low")}>
                        {task.due_date ? formatDateDDMMYYYY(task.due_date) : t("taskDetail.noDueDate")}
                      </span>
                    </div>
                  </SidebarField>
                )}

                {task.tags.length > 0 && (
                  <SidebarField label={t("taskDetail.tags")}>
                    <div className="flex flex-wrap gap-1.5">
                      {task.tags.map((tag) => (
                        <Badge key={tag.text} variant="soft" color={tag.color} text={tag.text} />
                      ))}
                    </div>
                  </SidebarField>
                )}

                {task.fsm_submission_id && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={handleOpenFsmReport}
                    disabled={isFsmPdfLoading}>
                    <Camera size={14} color="currentColor" />
                    {isFsmPdfLoading ? t("fsm.downloading") : t("fsm.downloadReport")}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isExportingAudit && (
        <Prompt
          loaderMode
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("export.loader.text")}
          additionalText={t("export.loader.additionalText")}
          namespace="TasksPage"
        />
      )}

      {exportError && (
        <Prompt
          title={t("messages.errorTitle")}
          text={exportError}
          variant="error"
          onClose={() => setExportError(null)}
          namespace="TasksPage"
        />
      )}
    </ModalForm>

    {fsmPdfUrl && (
      <PdfViewer
        fileUrl={fsmPdfUrl}
        title={t("fsm.reportTitle")}
        onClose={() => setFsmPdfUrl(null)}
      />
    )}
    </>
  );
}

interface SidebarFieldProps {
  label: string;
  children: React.ReactNode;
}

function SidebarField({ label, children }: SidebarFieldProps) {
  return (
    <div>
      <p className="text-label-xs content-base-secondary mb-1">{label}</p>
      {children}
    </div>
  );
}

